// Per-origin-hub branch flow actuals — (origin hub, branch, date) → volume.
// Drives the network-flow branch forecast: a branch is fed by its home hub
// (Local) plus H2H bits from every other hub, with each origin's share derived
// from this data.

import { supabase } from "../../lib/supabase";
import { fetchAllPaged } from "../../lib/dbPaginate";

export interface BranchFlow {
  originHubId: string;
  branchId: string;
  date: string;
  volume: number;
}

interface BranchFlowRow {
  origin_hub_id: string;
  branch_id: string;
  date: string;
  volume: number;
}

export function rowToBranchFlow(r: BranchFlowRow): BranchFlow {
  return { originHubId: r.origin_hub_id, branchId: r.branch_id, date: r.date, volume: Number(r.volume) };
}

/**
 * Aggregated per-origin → branch totals over a window (one row per origin/branch),
 * via the `flow_shares` RPC — far cheaper than pulling every daily row just to
 * derive shares. `date` on the returned rows is unused (set to the window start).
 */
export async function getFlowShareTotals(accountId: string, startDate: string, endDate: string): Promise<BranchFlow[]> {
  const { data, error } = await supabase.rpc("flow_shares", {
    p_account: accountId,
    p_start: startDate,
    p_end: endDate,
  });
  if (error) throw error;
  return ((data as { origin_hub_id: string; branch_id: string; volume: number }[]) ?? []).map((r) => ({
    originHubId: r.origin_hub_id,
    branchId: r.branch_id,
    date: startDate,
    volume: Number(r.volume),
  }));
}

/** Load per-origin branch flow in a date range (paged — this dataset is large). */
export async function listAccountBranchFlow(accountId: string, startDate: string, endDate: string): Promise<BranchFlow[]> {
  const rows = await fetchAllPaged<BranchFlowRow>((from, to) =>
    supabase
      .from("branch_flow_actuals")
      .select("origin_hub_id,branch_id,date,volume")
      .eq("account_id", accountId)
      .gte("date", startDate)
      .lte("date", endDate)
      .order("id")
      .range(from, to)
  );
  return rows.map(rowToBranchFlow);
}
